import { useState } from 'react';
import { CheckIcon, ClipboardIcon } from '@heroicons/react/20/solid';
import cn from './cn.js';
import { copyToClipboard } from './toast.js';

/** Small copy-to-clipboard button with a transient "copied" state. */
export default function CopyButton({ value, label = 'Copy', message = 'Copied to clipboard', className, size = 'sm', children, tone = 'default' }) {
  const [copied, setCopied] = useState(false);

  const handle = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    const ok = await copyToClipboard(value, message);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  const iconClass = size === 'xs' ? 'h-3.5 w-3.5' : 'h-4 w-4';

  return (
    <button
      type="button"
      onClick={handle}
      title={label}
      aria-label={label}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-md transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        tone === 'inverted' ? 'text-gray-300 hover:bg-white/10 hover:text-white' : 'text-gray-400 hover:text-indigo-600',
        children ? 'px-1.5 py-0.5 text-xs font-medium' : size === 'xs' ? 'p-0.5' : 'p-1',
        copied && (tone === 'inverted' ? 'text-emerald-300 hover:text-emerald-300' : 'text-emerald-600 hover:text-emerald-600'),
        className,
      )}
    >
      {copied ? <CheckIcon className={iconClass} aria-hidden="true" /> : <ClipboardIcon className={iconClass} aria-hidden="true" />}
      {children}
    </button>
  );
}

/** Monospace chip for ids / keys with a copy button. */
export function IdChip({ value, label, className, copyMessage, truncate = true, prefix }) {
  return (
    <span className={cn('inline-flex max-w-full items-center gap-1.5 rounded-md bg-gray-50 px-2 py-1 text-xs text-gray-600 ring-1 ring-inset ring-gray-200', className)} title={String(value ?? '')}>
      {prefix && <span className="font-medium text-gray-500">{prefix}</span>}
      <span className={cn('font-mono text-gray-700', truncate && 'truncate')}>{value}</span>
      <CopyButton value={value} label={label || 'Copy'} message={copyMessage || 'Copied to clipboard'} size="xs" />
    </span>
  );
}
